<template>
  <Teleport to="body">
    <div
      class="UIModuleModal adaptive-colors theme-white"
      :class="[{ 'is-open': isOpen }, extraClass]"
    >
      <div
        class="modal-bg"
        @click="emit('close')"
      />
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ title }}</h3>
          <button
            :aria-label="'Sluiten'"
            class="close-btn"
            @click="emit('close')"
          >
            <UIIconComponent icon="close" />
          </button>
        </div>
        <div
          class="modal-body"
          :class="{ 'has-overflow': hasOverflow, 'is-scrolled-to-bottom': isScrolledToBottom }"
        >
          <div
            ref="modalBody"
            class="body-inside"
            @scroll="onBodyScroll"
          >
            <slot name="body" />
          </div>
        </div>
        <div class="modal-footer">
          <button
            v-if="primaryLabel"
            class="btn btn-primary m-0"
            @click="emit('primary')"
          >
            <span class="text">{{ primaryLabel }}</span>
          </button>
          <button
            v-if="secondaryLabel"
            class="btn btn-secondary m-0 order--1"
            @click="emit('secondary')"
          >
            <span class="text">{{ secondaryLabel }}</span>
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
<script lang="ts" setup>
  defineProps<{
    isOpen: boolean
    title: string
    primaryLabel?: string
    secondaryLabel?: string
    extraClass?: string
  }>()

  const emit = defineEmits<{
    close: []
    primary: []
    secondary: []
  }>()

  const modalBody = ref<HTMLElement | null>(null)
  const hasOverflow = ref(false)
  const isScrolledToBottom = ref(false)

  function checkOverflow() {
    const el = modalBody.value
    if (!el) return
    hasOverflow.value = el.scrollHeight > el.clientHeight
    isScrolledToBottom.value = hasOverflow.value && el.scrollTop + el.clientHeight >= el.scrollHeight - 1
  }

  function onBodyScroll() {
    const el = modalBody.value
    if (!el) return
    isScrolledToBottom.value = el.scrollTop + el.clientHeight >= el.scrollHeight - 1
  }

  let stopWatch: (() => void) | null = null
  let resizeObserver: ResizeObserver | null = null

  onMounted(() => {
    stopWatch = watch(
      () => modalBody.value,
      (el) => {
        if (!el) return
        resizeObserver?.disconnect()
        resizeObserver = new ResizeObserver(checkOverflow)
        resizeObserver.observe(el)
        checkOverflow()
      },
      { immediate: true },
    )
  })

  onUnmounted(() => {
    if (stopWatch) stopWatch()
    resizeObserver?.disconnect()
  })
</script>
<style scoped lang="scss">
  .UIModuleModal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100dvh;
    justify-content: center;
    align-items: flex-end;
    z-index: 1000;
    pointer-events: none;
    display: flex;

    .modal-bg {
      position: absolute;
      transition: 0.4s all var(--ease-fast-in-slow-out);
      transition-delay: 0.1s;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      background-color: rgba(0, 0, 0, 0.5);
    }

    .modal-content {
      background-color: #fff;
      color: $color-text;
      padding: var(--padding-lg);
      border-radius: var(--border-radius-lg) var(--border-radius-lg) 0 0;
      width: 100%;
      max-height: 90dvh;
      z-index: 10;
      transition: 0.4s all var(--ease-fast-in-slow-out);
      flex-direction: column;
      display: flex;
      transform: translateY(100%);
    }

    .close-btn {
      font-size: 2.4rem;
    }

    &.is-open {
      pointer-events: all;

      .modal-content {
        transform: translateY(0);
      }

      .modal-bg {
        opacity: 1;
      }
    }

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
    }

    .modal-body {
      position: relative;
      display: flex;
      overflow: hidden;
      flex: 1;
      padding: 0 2px 2px;
      flex-direction: column;

      .body-inside {
        overflow-y: auto;
        flex: 1;
        flex-direction: column;
        display: flex;
      }

      &.has-overflow {
        &:after {
          content: '';
          position: absolute;
          top: 100%;
          left: 0;
          pointer-events: none;
          width: 100%;
          transform: translateY(calc(-100% + 5px));
          height: 10rem;
          transition:
            0.5s opacity var(--ease-fast-in-slow-out),
            0.5s transform var(--ease-fast-in-slow-out);
          background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 100%);
        }

        &.is-scrolled-to-bottom {
          &:after {
            transform: translateY(0);
            opacity: 0;
          }
        }
      }
    }

    .modal-footer {
      display: flex;
      justify-content: flex-end;
      padding-top: 1.6rem;
      gap: var(--margin-sm);
    }
  }
</style>
